import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const MathBingoGame = ({ gameData, onComplete }) => {
    const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
    const [markedCells, setMarkedCells] = useState([]);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [gameWon, setGameWon] = useState(false);
    const [shuffledProblems, setShuffledProblems] = useState([]);

    useEffect(() => {
        if (gameData?.problems) {
            setShuffledProblems([...gameData.problems].sort(() => Math.random() - 0.5));
        }
    }, [gameData]);

    const currentProblem = shuffledProblems?.[currentProblemIndex];
    const gridSize = gameData?.grid_size || 5;

    const checkWinCondition = (marked) => {
        // Check rows
        for (let row = 0; row < gridSize; row++) {
            let rowComplete = true;
            for (let col = 0; col < gridSize; col++) {
                if (!marked.some(m => m.row === row && m.col === col)) {
                    rowComplete = false;
                    break;
                }
            }
            if (rowComplete) return true;
        }
        // Check columns
        for (let col = 0; col < gridSize; col++) {
            let colComplete = true;
            for (let row = 0; row < gridSize; row++) {
                if (!marked.some(m => m.row === row && m.col === col)) {
                    colComplete = false;
                    break;
                }
            }
            if (colComplete) return true;
        }
        // Check diagonals
        let diagonal1 = true;
        let diagonal2 = true;
        for (let i = 0; i < gridSize; i++) {
            if (!marked.some(m => m.row === i && m.col === i)) diagonal1 = false;
            if (!marked.some(m => m.row === i && m.col === gridSize - 1 - i)) diagonal2 = false;
        }
        return diagonal1 || diagonal2;
    };

    const handleCellClick = (row, col) => {
        if (feedback || gameWon) return;
        const cellValue = gameData.grid[row][col];
        const isCorrect = cellValue === currentProblem.answer;

        triggerHaptic(30);

        if (isCorrect) {
            const newMarked = [...markedCells, { row, col, value: cellValue }];
            setMarkedCells(newMarked);
            setFeedback('correct');
            const points = gameData.points_per_correct || 10;
            const newScore = score + points;
            setScore(newScore);

            if (checkWinCondition(newMarked)) {
                setGameWon(true);
                triggerHaptic([100, 50, 100, 50, 300]);
                setTimeout(() => onComplete?.(newScore), 4000);
            } else {
                triggerHaptic([40, 40]);
                setTimeout(() => {
                    setFeedback(null);
                    if (currentProblemIndex + 1 < shuffledProblems.length) {
                        setCurrentProblemIndex(prev => prev + 1);
                    }
                }, 2000);
            }
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => setFeedback(null), 1500);
        }
    };

    const isCellMarked = (row, col) => markedCells.some(m => m.row === row && m.col === col);

    if (!gameData || !currentProblem) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-indigo-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🎰</div>
                <h3 className="text-base md:text-3xl font-black text-indigo-900 tracking-tighter uppercase">Ruošiama Bingo korta...</h3>
            </div>
        );
    }

    const progress = (currentProblemIndex / shuffledProblems.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-indigo-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-indigo-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🎰
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Matematikos Bingo
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Aukso Puodas</p>
                        <p className="text-sm md:text-2xl font-black text-indigo-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Eiga</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentProblemIndex + 1}/{shuffledProblems.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-indigo-400 via-purple-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(99,102,241,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-12">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-14 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center text-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-[150px] opacity-[0.03] select-none pointer-events-none text-indigo-900">➕</div>
                <h4 className="text-[8px] md:text-[10px] font-black text-indigo-400 uppercase tracking-[0.5em] mb-6">Dabartinė Užduotis</h4>
                
                <div className="bg-indigo-900 rounded-[2.5rem] md:rounded-[3.5rem] p-8 md:p-16 border-4 md:border-8 border-white shadow-2xl relative group overflow-hidden w-full max-w-xl">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.2)_0%,transparent_100%)] pointer-events-none" />
                    <h3 className="text-4xl md:text-8xl font-black text-white tracking-tighter leading-tight drop-shadow-2xl animate-pop-in relative z-10">
                        {currentProblem.question}
                    </h3>
                    <div className="mt-4 md:mt-8 pt-4 md:pt-8 border-t-2 md:border-t-4 border-dashed border-white/10 relative z-10">
                        <p className="text-xl md:text-3xl font-black text-indigo-300 italic font-serif">
                            = <span className="animate-pulse text-white">?</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-6 flex flex-col items-center w-full max-w-4xl mx-auto">
            <div className="flex items-center gap-2 px-4 self-start">
                <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Tavo Bingo Korta</h4>
            </div>
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-3 md:p-10 shadow-2xl border-white/60 bg-slate-900/90 relative overflow-hidden w-full flex flex-col items-center min-h-[300px] justify-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 opacity-[0.03] text-6xl md:text-[200px] font-black select-none pointer-events-none text-white">🎰</div>
                
                <div 
                    className="grid gap-2 md:gap-4 relative z-10 w-full" 
                    style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}
                >
                    {gameData.grid.map((row, rowIndex) =>
                        row.map((cell, colIndex) => {
                            const marked = isCellMarked(rowIndex, colIndex);
                            
                            return (
                                <button
                                    key={`${rowIndex}-${colIndex}`}
                                    onClick={() => handleCellClick(rowIndex, colIndex)}
                                    disabled={marked || gameWon}
                                    className={`
                                        relative aspect-square rounded-xl md:rounded-[1.5rem] text-sm md:text-4xl font-black
                                        transition-all duration-300 transform border-b-4 md:border-b-8
                                        ${marked
                                            ? 'bg-emerald-500 text-white border-emerald-700 shadow-inner scale-95 border-b-0'
                                            : 'bg-white text-slate-800 border-slate-200 hover:bg-indigo-50 active:translate-y-1 active:border-b-0 shadow-lg'}
                                        disabled:cursor-default
                                    `}
                                >
                                    <div className={`relative z-10 ${marked ? 'scale-110' : ''}`}>
                                        {marked ? '✓' : cell}
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {gameWon && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className="max-w-xl w-full p-8 md:p-16 rounded-[2.5rem] md:rounded-[5rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px] border-indigo-500 text-center">
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl shadow-2xl border-4 border-indigo-500 animate-bounce">
                            🎰
                        </div>
                        
                        <div className="space-y-6 pt-6">
                            <h2 className="text-4xl md:text-7xl font-black uppercase tracking-tighter text-indigo-600 leading-none">
                                BINGO!
                            </h2>
                            <div className="p-6 md:p-10 rounded-2xl md:rounded-[3rem] border-2 bg-indigo-50 border-indigo-100">
                                <p className="text-base md:text-2xl font-bold text-slate-700 leading-relaxed italic text-center font-serif">
                                    "Nuostabu! Išpildei Bingo liniją ir tapai čempionu!"
                                </p>
                            </div>
                            <div className="flex items-center justify-center gap-4">
                                <div className="flex gap-2">
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce" />
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                                <span className="text-[10px] md:text-sm font-black text-slate-300 uppercase tracking-[0.3em]">Registruojama...</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {feedback && !gameWon && (
                <div className="fixed inset-0 pointer-events-none flex items-center justify-center z-[2000] p-4">
                    <div className={`
                        px-10 py-8 md:px-20 md:py-16 rounded-[2rem] md:rounded-[4rem] shadow-2xl backdrop-blur-2xl border-4 md:border-8 animate-pop-in
                        ${feedback === 'correct' ? 'bg-emerald-500/90 border-emerald-400 text-white' : 'bg-rose-500/90 border-rose-400 text-white'}
                    `}>
                        <div className="text-6xl md:text-[120px] text-center mb-4 animate-bounce">
                            {feedback === 'correct' ? '🎯' : '❌'}
                        </div>
                        <p className="text-xl md:text-5xl font-black text-center tracking-tighter uppercase">
                            {feedback === 'correct' ? 'TIKSLU!' : 'KLAIDA!'}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};

export default MathBingoGame;
